import invariant from 'tiny-invariant';

import { sharedOptions } from 'src/modules/designStudio/machines/sharedOptions';
import { client as apolloClient } from 'src/shared/graphql';
import { mutations } from 'src/shared/graphql/requests';
import { MobileAppActionEnum } from 'src/shared/hooks/useReactNativeWebView/types';

const JOB_ID_REQUIRED_ERROR_MESSAGE = 'jobId is required';

const openURL = ({
  url,
  isWebView,
  target,
  sendReactNativeMessage,
}: {
  url: string;
  isWebView: boolean;
  target?: string;
  sendReactNativeMessage?: (message: string) => void;
}) =>
  isWebView && sendReactNativeMessage
    ? sendReactNativeMessage(
        JSON.stringify({ action: MobileAppActionEnum.OPEN_LINK, data: url }),
      )
    : window.open(url, target);

/* Navigates to the property details page where the user can see their
 * existing rendering for the given job */
const openExistingRenderings = ({
  isWebView,
  sendReactNativeMessage,
  jobId,
}: {
  isWebView: boolean;
  sendReactNativeMessage?: (message: string) => void;
  jobId?: number;
}) => {
  invariant(jobId, JOB_ID_REQUIRED_ERROR_MESSAGE);
  openURL({
    url: `${window.location.protocol}//${window.location.host}/ui/#/property/${jobId}`,
    isWebView,
    target: '_blank',
    sendReactNativeMessage,
  });
};

/* Creates a new rendering record in hyperion and returns the rendering request form URL.
 * Then opens the URL in either a webview or a new window */
const startNewRendering = async ({
  isWebView,
  jobId,
  setIsLoading = () => null,
  sendReactNativeMessage,
}: {
  isWebView: boolean;
  jobId?: number;
  hasRenderings?: boolean;
  setIsLoading?: (loading: boolean) => void;
  sendReactNativeMessage?: (message: string) => void;
}) => {
  invariant(jobId, JOB_ID_REQUIRED_ERROR_MESSAGE);
  let requestFormUrl;
  setIsLoading(true);

  try {
    const { data } = await mutations.createHiDefRendering(apolloClient, {
      jobId: jobId.toString(),
    });

    requestFormUrl = data?.createHiDefRendering.requestFormUrl;
  } catch (e: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    sharedOptions.logError(undefined, e);
  }

  if (isWebView) {
    if (requestFormUrl) {
      openURL({ url: requestFormUrl, isWebView, sendReactNativeMessage });
    }
    return setIsLoading(false);
  }

  // Open blank window immediately to ensure popup is not blocked due
  // to delay between user input and call to window.open
  const formWindow = window.open('', 'hi_def_rendering_form');

  if (formWindow) {
    formWindow.document.write('Loading form...');
  }

  if (!!formWindow && requestFormUrl) {
    formWindow.location.href = requestFormUrl;
  } else {
    formWindow?.close();
  }

  return setIsLoading(false);
};

export { openExistingRenderings, startNewRendering };
